<script setup>
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { Badge } from '@dashhudson/dashing-ui/badge';
import { ChannelIcon } from '@dashhudson/dashing-ui/channel-icon';
import { Icon } from '@dashhudson/dashing-ui/icon';
import { SelectButton } from '@dashhudson/dashing-ui/select-button';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import { isEqual } from 'lodash-es';
import { computed, onUnmounted, ref, watch } from 'vue';

const emit = defineEmits([
  'update:modelValue',
  'update:selectButtonValue',
  'keydown',
  'valid',
  'commandName',
  'commandTitle',
  'reset',
]);

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    required: true,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  disableSelectAll: {
    type: Boolean,
    default: false,
  },
  disableBranchSelectAll: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  selectButtonValue: {
    type: String,
    default: null,
  },
  selectButtonOptions: {
    type: Array,
    default: () => [],
  },
  showSelectButton: {
    type: Boolean,
    default: false,
  },
  dataTest: {
    type: String,
    default: undefined,
  },
  ignoreAvatar: {
    type: Boolean,
    default: false,
  },
  totalFilterCount: {
    type: Number,
    default: 0,
  },
  virtualScroller: {
    type: Object,
    default: null,
  },
  filterType: {
    type: String,
    default: 'default',
  },
});

// Helper functions
const cloneValue = (value) => (Array.isArray(value) ? [...value] : value);

// State management
const initialValue = ref(cloneValue(props.modelValue));
const currentValue = ref(cloneValue(props.modelValue));
const initialSelectButtonValue = ref(props.selectButtonValue);
const currentSelectButtonValue = ref(props.selectButtonValue);

const hasChanged = computed(
  () =>
    !isEqual(initialValue.value, currentValue.value) ||
    !isEqual(initialSelectButtonValue.value, currentSelectButtonValue.value),
);

function updateButtonState() {
  // Calculate live total: committed filters from other tabs + current tab's live selections
  const currentSelectionCount = Array.isArray(currentValue.value) ? currentValue.value.length : 0;
  const liveTotal = props.totalFilterCount + currentSelectionCount;

  const hasUncommittedChanges = hasChanged.value || props.totalFilterCount > 0;
  emit('valid', hasUncommittedChanges);
  emit('commandName', liveTotal > 0 ? `Apply (${liveTotal})` : 'Apply');
}

function handleSelectButtonUpdate(value) {
  currentSelectButtonValue.value = value;
  emit('update:selectButtonValue', value);
}

function handleModelUpdate(value) {
  currentValue.value = value;
  emit('update:modelValue', value);
}

onUnmounted(() => {
  if (hasChanged.value) {
    emit('reset');
  }
});

// Watcher for all state changes that affect button state
watch([hasChanged, currentValue], updateButtonState, { immediate: true });

// Prop watchers to sync external changes
watch(
  () => props.modelValue,
  (newValue) => {
    const cloned = cloneValue(newValue);
    initialValue.value = cloned;
    currentValue.value = cloned;
  },
  { immediate: true },
);

watch(
  () => props.selectButtonValue,
  (newValue) => {
    initialSelectButtonValue.value = newValue;
    currentSelectButtonValue.value = newValue;
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="showSelectButton" class="mb-2 flex">
    <SelectButton
      variant="light"
      full-width
      :model-value="currentSelectButtonValue"
      :options="selectButtonOptions"
      :allow-empty="false"
      @update:model-value="handleSelectButtonUpdate"
    />
  </div>

  <SelectList
    :key="`select-list-${filterType}`"
    :model-value="currentValue"
    :options="options"
    :multiple="multiple"
    :searchable="searchable"
    :disable-select-all="disableSelectAll"
    :disable-branch-select-all="disableBranchSelectAll"
    :disabled="disabled"
    :data-test="dataTest"
    :virtual-scroller="virtualScroller"
    @update:model-value="handleModelUpdate"
  >
    <!-- Custom option label based on detected properties -->
    <template #optionLabel="{ option, label }">
      <div class="!flex !items-center !gap-2">
        <!-- Show badge with severity if option has severity property -->
        <Badge v-if="option.severity" dot :severity="option.severity" />
        <!-- Show badge with colored dot if option has color property -->
        <Badge v-else-if="option.color" :style="{ backgroundColor: option.color }" />
        <!-- Show avatar if option has avatarUrl property -->
        <Avatar
          v-else-if="option.avatarUrl && !props.ignoreAvatar"
          size="xs"
          :image="option.avatarUrl"
        />
        <!-- Show channel icon if option has channel property -->
        <ChannelIcon v-else-if="option.channel" :channel="option.channel" :size="16" />
        <!-- Show icon if option has icon property -->
        <Icon v-else-if="option.icon" :name="option.icon" :size="12" />

        <span class="overflow-hidden truncate">{{ label }}</span>
      </div>
    </template>
  </SelectList>
</template>
